import getEventBoxHeight from '@sdk/schedule/helpers/getEventBoxHeight';

import type { User, DynamicGroup } from '../../types/GroupsArrayType';
import type { RowType } from '../../types/RowType';
import type { OptionsFilterType } from '../../types';
import shallowCopyEvents from '../helpers/shallowCopyEvents';
import generateRowId from '../helpers/generateRowId';
import getUniqueRowId from '../helpers/getUniqueRowId';
import { EVENT_TEXT_LINE_HEIGHT, BOX_VERTICAL_PADDING } from '../constants';

import getRowEvents from './getRowEvents';
import getRegularRows from './getRegularRows';
import getRowHeight from './getRowHeight';
import { markLastRow } from './markLastRow';

type Config = {
  eventHeight: number;
  hideEmptyRows: boolean;
  rowIds: { [key in string]: boolean };
  eventNumberOfLines: number;
  optionsFilter: OptionsFilterType;
};

export default function getUserRows(
  user: User,
  group: DynamicGroup,
  config: Config
): Array<RowType> {
  const rows = [];
  const { eventNumberOfLines, eventHeight, hideEmptyRows, rowIds } = config;
  const events = shallowCopyEvents(user.events);
  const rowEvents = getRowEvents(events);
  const userInactive = !user.active || user.deleted;
  const hasEvents = Object.keys(rowEvents).length;

  if (userInactive && !hasEvents) {
    return rows;
  }

  if (!hideEmptyRows || hasEvents) {
    const idPrefix = `FirstRow_${user.id}_${group.type}_${group.key}`;
    const { showLaborCost, showForeignShifts, showCountOfHours } = config.optionsFilter;
    const shouldAdjustFirstRowHeight =
      showLaborCost && showForeignShifts && showCountOfHours && eventNumberOfLines < 3;

    const minimalFirstRowEventHeight = getEventBoxHeight(
      shouldAdjustFirstRowHeight ? 3 : eventNumberOfLines,
      {
        lineHeight: EVENT_TEXT_LINE_HEIGHT,
        verticalPadding: BOX_VERTICAL_PADDING,
      }
    );

    rows.push({
      id: getUniqueRowId(rowIds, generateRowId(idPrefix, rowEvents)),
      rowType: 'RegularFirstRow',
      rowData: {
        events: rowEvents,
        size: getRowHeight(minimalFirstRowEventHeight, { isFirst: true }),
        eventHeight: eventHeight,
        user,
        group,
      },
    });
    rows.push(
      ...getRegularRows({
        events,
        user,
        group,
        eventHeight,
        rowIds,
      })
    );
  }

  markLastRow(rows);

  return rows;
}
